<template>
  <a class="home-event-card" :href="eventUrl" @click.prevent="$emit('select')">
    <div
      class="home-event-card-image"
      :style="imageUrl ? { backgroundImage: `url('${imageUrl}')` } : undefined"
    >
      <span v-if="activityName" class="home-event-card-tag">
        {{ activityName }}
      </span>
    </div>
    <div class="home-event-card-body">
      <h3 class="home-event-card-title">{{ name }}</h3>
      <p class="home-event-card-meta">
        {{ date }} · {{ startTime }}
        <br />
        {{ locationLabel }}
      </p>
      <div class="home-event-card-footer">
        <span
          class="home-event-card-spots"
          :class="{ 'home-event-card-spots-full': isFull }"
        >
          {{ spotsLabel }}
        </span>
        <span
          class="home-event-card-price"
          :class="{ 'home-event-card-price-free': !hasCost }"
        >
          <template v-if="hasCost">{{ $n(costNumber, 'currency') }}</template>
          <template v-else>{{ $t('home.events.free') }}</template>
        </span>
      </div>
    </div>
  </a>
</template>

<script>
export default {
  name: 'HomeEventCard',
  props: {
    name: {
      type: String,
      required: true
    },
    activityName: {
      type: String,
      required: false,
      default: ''
    },
    imageUrl: {
      type: String,
      required: false,
      default: null
    },
    date: {
      type: String,
      required: false,
      default: ''
    },
    startTime: {
      type: String,
      required: false,
      default: ''
    },
    locationName: {
      type: String,
      required: false,
      default: ''
    },
    city: {
      type: String,
      required: false,
      default: ''
    },
    availableSlots: {
      type: Number,
      required: false,
      default: 0
    },
    maximumParticipants: {
      type: Number,
      required: false,
      default: 0
    },
    cost: {
      type: [String, Number],
      required: false,
      default: '0'
    },
    dateId: {
      type: Number,
      required: false,
      default: null
    },
    securityHash: {
      type: String,
      required: false,
      default: ''
    }
  },
  computed: {
    eventUrl() {
      return this.dateId && this.securityHash
        ? `/events/${this.dateId}/${this.securityHash}?origin=home`
        : '/events'
    },
    locationLabel() {
      return [this.locationName, this.city].filter(Boolean).join(', ')
    },
    costNumber() {
      return Number(this.cost) || 0
    },
    hasCost() {
      return this.costNumber > 0
    },
    hasParticipantLimit() {
      return this.maximumParticipants > 0
    },
    isFull() {
      return this.hasParticipantLimit && this.availableSlots <= 0
    },
    spotsLabel() {
      if (!this.hasParticipantLimit) {
        return this.$t('home.events.openRegistration')
      }
      if (this.isFull) return this.$t('home.events.full')
      return this.$tc('home.events.spotsLeft', this.availableSlots)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.home-event-card {
  display: block;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  overflow: hidden;
  color: inherit;
  text-decoration: none;
  transition: box-shadow 0.18s, transform 0.18s;

  &:hover {
    box-shadow: 0 10px 28px rgba(68, 74, 73, 0.1);
    transform: translateY(-2px);
    color: inherit;
    text-decoration: none;
  }
}

.home-event-card-image {
  position: relative;
  height: 148px;
  background-color: #edf1f0;
  background-size: cover;
  background-position: center;
}

.home-event-card-tag {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #fff;
  border-radius: 9999px;
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: $ipBlack;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.home-event-card-body {
  padding: 1.125rem;
}

.home-event-card-title {
  margin: 0;
  font-size: 1.03rem;
  font-weight: 600;
  color: $ipBlack;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.home-event-card-meta {
  margin: 0.5rem 0 0;
  font-size: 0.84rem;
  color: $ipGreyDark;
  line-height: 1.5;
}

.home-event-card-footer {
  margin-top: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.home-event-card-spots {
  background: #e8f6f1;
  color: #00805a;
  border-radius: 9999px;
  padding: 4px 11px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

.home-event-card-spots-full {
  background: $greyLight;
  color: $ipGreyDark;
}

.home-event-card-price {
  font-size: 0.9375rem;
  font-weight: 700;
  color: $ipBlack;
}

.home-event-card-price-free {
  color: $ipGreen;
}
</style>
